import { useCallback, useState } from 'react';

import { useSelector, useStore } from 'react-redux';
import config from '../config';

import { RootState } from '../types/graph';

import { client } from '../util/client';
import { useVersion } from './useVersion';
import { useMode } from './useMode';
import { useDetermineNewPatient } from './questionnaire.hooks';

export interface QuestionnaireStatistic {
  section: string;
  version: string;
  numberOfQuestions?: number;
  numberOfResponses?: number;
}

export const useStatistics = () => {
  const sessionId = useSelector((state: RootState) => state.sessionId);
  const demoPractice = useSelector((state: RootState) =>
    Boolean(state.consultation.practice?.demo),
  );
  const store = useStore<RootState>();
  const variant = useSelector((state: RootState) => state.graph.variant);
  const noAppointment = useSelector(
    (state: RootState) => state.consultation.noAppointment,
  );
  const newPatient = useDetermineNewPatient();
  const invitationFallback = useSelector(
    (state: RootState) => state.invitationFallback,
  );
  const mode = useMode();
  const [startTime, setStartTime] = useState(new Date());
  const version = useVersion();

  const demo = config.env.demo || demoPractice;

  return useCallback(
    (data: Omit<QuestionnaireStatistic, 'version'>, disabled?: boolean) => {
      if (demo || disabled) return;

      client
        .post('/questionnaireStatistics/v2', {
          sessionId,
          data: [
            {
              startTime,
              endTime: new Date(),
              demo: demo || disabled,
              version: version?.toString(),
              // The derived referrer and first two statistics rows run in one
              // effect, so read it at call time instead of closing over state.
              referrer: store.getState().referrer,
              mode,
              variant,
              noAppointment,
              newPatient,
              invitationFallback,
              ...data,
            },
          ],
        })
        .finally(() => setStartTime(new Date()));
      return;
    },
    [
      sessionId,
      demo,
      version,
      store,
      mode,
      variant,
      noAppointment,
      newPatient,
      invitationFallback,
    ],
  );
};
